import { useCallback, useEffect, useMemo, useRef, useState } from "react";

// Redux
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";

// Context
import { useSubscriptions } from "@Common/context/subscriptions/Subscriptions";

// Hooks
import useCacheCleaner from "@/Common/hooks/CacheCleaner";

// SUBSCRIPTIONS
import { onUpdateCallActivity } from "@/DataPages/api/callActivity/subscriptions";

// Components
import BodyLayer from "@Layouts/components/Layers/BodyLayer";
import CommissionSideBar from "@Layouts/components/SideBars/CommissionSideBar";
import CollapsibleLeftPanel from "@Layouts/components/CollapsibleLeftPanel";
import RoundedBox from "@Layouts/components/Basics/RoundedBox";
import CallActivityModel from "@/DataPages/components/CallActivity/components/CallActivityModel";

// Types
import { ISidebarItem } from "@Common/interfaces/sidebar";

const QUERY_IDENTIFIERS = ["callActivityCache"];

interface ICallActivityLayerProps {
	userId?: string;
}
export default function CallActivityLayer(props: Readonly<ICallActivityLayerProps>) {
	const { userId } = props;
	const [selection, setSelection] = useState<ISidebarItem | null>(null);
	const lastSelected = useRef<ISidebarItem | null>(null);
	const open = useRef(true);
	const { clearCache } = useCacheCleaner();

	const { addSubscription, clearSubscriptionContext } = useSubscriptions();
	const location = useSelector((state: RootState) => state.location.location);
	const locationId = location?.locationId;

	const isEqualSelection = (selected: ISidebarItem) => selected?._id === lastSelected?.current?._id;

	const toggleOpenState = () => {
		open.current = !open.current;
	};

	const updateLastSelected = (selected: ISidebarItem) => {
		lastSelected.current = selected;
	};

	const handleUpdateSelection = (selected: ISidebarItem) => {
		setSelection(selected);
	};

	const addCallActivitySubscription = (selected: ISidebarItem) => {
		addSubscription(
			["callActivityReportSubscription", locationId, selected._id],
			[
				{
					context: "onUpdateCallActivity",
					query: onUpdateCallActivity,
					variables: {
						locationId,
						commissionCycleId: selected._id,
					},
				},
			]
		);
	};

	const handleSelection = useCallback(
		(selected: ISidebarItem, isActualCycle: boolean) => {
			if (isEqualSelection(selected)) {
				return;
			}
			toggleOpenState();
			updateLastSelected(selected);
			handleUpdateSelection(selected);

			if (!isActualCycle) {
				return;
			}
			addCallActivitySubscription(selected);
		},
		[addSubscription, locationId]
	);

	const sideBar = useMemo(() => {
		return <CommissionSideBar callbackSelected={handleSelection} selectDefaultLastCommissionCycle />;
	}, [addSubscription, locationId]);

	useEffect(() => {
		return () => {
			lastSelected.current = null;
			open.current = true;
			setSelection(null);
			clearSubscriptionContext();
			clearCache(QUERY_IDENTIFIERS);
		};
	}, [props, location, clearSubscriptionContext]);

	return (
		<BodyLayer floatingContent={<CollapsibleLeftPanel permanentTrigger={open.current} control={sideBar} />}>
			<RoundedBox sx={{ minHeight: "630px" }}>
				<CallActivityModel selected={selection} locationId={locationId} isUpdatable={!userId} userId={userId} />
			</RoundedBox>
		</BodyLayer>
	);
}
